<template>
  <div>
    <span id="1-network-elements-page" class="anchor"></span>
    <h3>1. Network Elements Page</h3>
    <p>
      This page (Fig. 18a) gives the list of network elements that are being
      monitored on the network. Ennetix xVisor supports various standard protocols
      to gather a network element's traffic and operations data. Some of these
      protocols are packet capture, NetFlow, SFlow, Syslog, SNMP, etc. The
      xVisor-user can utilize the search box at the top right corner by manually
      searching IP addresses from the list.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-network-elements-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-network-elements-page2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 18a: Ennetix xVisor Network Elements page.</p>
    <p>
      As shown in Fig. 18a, this page lists the network elements (classified by
      zones). Inside each zone, the network elements are sorted by the alert
      statistics. To get more information on an individual network element, the
      xVisor-user can either click the network element name or ">"
      symbol at the end of the row. This will bring the xVisor-user to the
      individual network element's page.
    </p>
    <span id="2-individual-network-element-page" class="anchor"></span>
    <h3>2. Individual Network Element Page</h3>
    <p>
      This page gives various statistics on applications, flows, and IPs on a
      network element. As shown in Fig. 18b, at the top of the page, there are three
      tiles. The xVisor-user can view Top Apps, Top Source IPs, and Top Interfaces
      by Utilization for the network element.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 18b: Ennetix xVisor Individual Network Element page - Part-1.</p>
    <p>
      The following panels (Figs. 19a-19c) serve as visual representations of
      input/output traffic load on a network element. The xVisor-user can use the
      time-selector option at the top right of the page to personalize the time
      period (6 hours, 24 hours, 2 days, or 1 week); visualizations provided in Fig.
      19a-19c are within a period of 6 hours. By clicking the tabs on "Traffic
      Profiles" panel, the xVisor-user can access various distributions of
      traffic profiles such as Total, Protocol, and Application.
    </p>
    <p>
      For "Total" distribution (Fig. 19a), there is a visualization of how
      much total traffic is flowing through the network element.
      "Protocol" distribution visualizes the types of protocol that can be
      seen on the network element (Fig. 19b). "Application" distribution
      tracks the application traffic visible within a network element (Fig. 19c).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page2.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 19a: Ennetix xVisor Individual Network Element page - Traffic Activity
      (In &amp; Out).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page3.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 19b: Ennetix xVisor Individual Network Element page - Traffic Activity
      (Protocol).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page4.png"
        class="responsive-img-lg"
      />
    </p>
    <p>
      Fig. 19c: Ennetix xVisor Individual Network Element page - Traffic Activity
      (Application).
    </p>
    <p>
      The Alerts panel (Fig. 19d) shows recently-raised alerts from the performance
      data collected from the network element.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page5.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 19d: Ennetix xVisor Individual Network Element page - Alerts Panel.</p>
    <p>
      At the top of the page, the xVisor-user can access more specific information
      about the network element within three tabs. "Description" tab (Fig.
      20) provides particular details about a network element, with the left column
      including its name, type, agent IP, vendor, model, and status.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page6.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 20: Ennetix xVisor Individual Network Element page - Description tab.</p>
    <p>
      "Flow Analysis" tab gives detailed flow analysis of the network
      element. The table on the left gives applications, their total traffic, top
      sources IPs using those applications, along with activity heatmap of the
      application traffic for the selected time period (Fig. 21a).
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page7.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 21a: Ennetix xVisor Individual Network Element page - Flows tab.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-flows-tab-diagram.png"
        class="responsive-img-xs"
      />
    </p>
    <p>Fig. 21b: Flows tab - Flow diagram.</p>
    <p>
      A very interesting visualization in the "Flow Analysis" tab is the
      "Flow Diagram". This visualization gives the xVisor-user information
      about where their traffic is flowing (which source/destination prefix and how
      much). Flows are aggregated using IP prefixes (Fig. 21b), which literally
      shows traffic activity coming from or going into multiple devices within an IP
      address block. The information flow diagrams list are protocol type,
      application type, and total amount of traffic traveling from source IP to
      destination IP prefixes or Applications.
    </p>
    <p>
      In the Interfaces table, interface-specific information is given for the
      selected time period (Fig. 22). This information includes status, name of the
      interface, SNMP index, top Apps visible on the interface, top source IPs using
      the interface, along with utilization heat map for the time period.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-individual-network-elements-page8.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig. 22: Ennetix xVisor Individual Network Element page - Interfaces tab.</p>
    <span id="3-xtends-page" class="anchor"></span>
    <h3>3. xTends Page</h3>
    <p>
      The xTend table (Fig. 22a) lists all devices running xTend and summarizes alert statistics for each
      device in a network environment, including the device name, IP address, and various alert metrics.
      When the user clicks on any device's name, the web page will redirect to show an individual xTend
      page for more detailed information about the currently selected device.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22a: Ennetix xVisor Individual xTend Page - Status.</p>
    <span id="4-individual-xtend-page" class="anchor"></span>
    <h3>4. Individual xTend Page</h3>
    <p>
      The status page (Fig. 22b) provides a comprehensive summary of the system's resource utilization
      and network status by three time series charts. It includes details on CPU usage,
      memory usage, and Wi-Fi signal strength. The status page offers valuable insights into
      the system's performance and connectivity. This data is essential for monitoring
      resource consumption, ensuring optimal performance, and maintaining a stable network
      connection.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-status-chart.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22b: Ennetix xVisor Individual xTend Page - Status.</p>
    <p>
      The processes table (Fig. 22c) provides an overview of the various processes running on the system.
      It includes key details such as the process name, user count, execution count,
      and parent process of each process. The table
      provides a comprehensive view of process activity and dependencies within the system.
      This data is essential for system administrators and users to monitor resource
      utilization, identify potential issues with processes that have high execution counts
      or user counts, and understand the hierarchical structure of process relationships.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-processes-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22c: Ennetix xVisor Individual xTend Page - Processes Table.</p>
    <p>
      The traffic table (Fig. 22d) provides a comprehensive overview of system traffic, detailing
      the program name, destination IPs, protocol used, and the total number of
      connections for each entry. The table offers
      valuable insights into the network activity on the system. It enables administrators
      and users to monitor which programs are generating traffic, identify the target
      destinations, understand the communication protocols in use, and assess the volume
      of connections. This data is crucial for maintaining network security, managing
      bandwidth usage, and optimizing network performance.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-traffic-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22d: Ennetix xVisor Individual xTend Page - Traffic Table.</p>
    <p>
      The packages table (Fig. 22e) provides a detailed overview of the packages installed on a system.
      It includes the package name, the date of installation, the version number,
      and the publisher of each package. The table
      helps users and administrators keep track of the software installed, ensuring
      that all packages are up-to-date and from reputable sources. This data is essential
      for system maintenance, security audits, and software management.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-packages-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22e: Ennetix xVisor Individual xTend Page - Packages Table.</p>
    <p>
      The alerts table (Fig. 22f1) provides a detailed overview of system alerts, categorizing
      each alert by its severity, the signature that triggered the alert, the
      count of occurrences, the number of affected users, and the number of programs
      involved. The table helps administrators
      quickly identify and prioritize critical issues, understand the scope of the
      impact, and allocate resources effectively for resolution. This data is essential
      for maintaining system security, ensuring prompt responses to potential threats,
      and managing overall system health.
      When the user clicks on a row of alerts table, A sub-table (Fig. 22f2) will show under the current
      table to provide additional details for each system alert, including the timestamp of the
      alert, the username associated with the alert, the program involved, and the parent process
      of the program.
    </p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-alert-summary-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22f1: Ennetix xVisor Individual xTend Page - Alerts Table.</p>
    <p>
      <img
        src="@/xvisor/views/knowledgeBase/assets/webPortal/ennetix-xtend-alert-details-table.png"
        class="responsive-img-lg"
      />
    </p>
    <p>Fig 22f2: Ennetix xVisor Individual xTend Page - Alerts Details Table.</p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      rootLink: window.location.hostname,
    };
  },
};
</script>

<style scoped>
.anchor {
  top: -75px;
  position: relative;
  z-index: 5;
}
</style>
